import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Phone, Mail, AlertCircle, Book, Shield, CreditCard, Users, Loader2,
} from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { useSiteContent } from '../../hooks/useSiteContent';

const ICON_MAP = {
  Users,
  Shield,
  CreditCard,
  Book,
};

const SiteHelpPage = () => {
  const navigate = useNavigate();
  const { page, loading, error } = useSiteContent('help');
  const data = page?.content_json || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-6xl mx-auto px-4 py-12 pt-24">
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">{error}</div>
        )}

        {!loading && page && (
          <>
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4">{page.title || data.title}</h1>
              <p className="text-gray-600 text-lg">{page.subtitle || data.subtitle}</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {(data.categories || []).map((category, index) => {
                const Icon = ICON_MAP[category.icon] || Book;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => navigate(category.link || '/faq')}
                    className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-2xl transition text-left"
                  >
                    <div className="bg-purple-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <Icon size={24} className="text-purple-600" />
                    </div>
                    <h3 className="font-bold text-lg mb-2">{category.title}</h3>
                    <p className="text-gray-600 text-sm">{category.description}</p>
                  </button>
                );
              })}
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6">Popular Topics</h2>
                <div className="space-y-4">
                  {(data.popular_topics || []).map((topic, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => navigate('/faq')}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition"
                    >
                      <p className="font-semibold">{topic}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-6">
                {data.emergency && (
                  <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-6">
                    <div className="flex items-start space-x-3">
                      <AlertCircle size={24} className="text-red-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-red-800 mb-2">{data.emergency.title}</h3>
                        <p className="text-red-700 text-sm mb-3">{data.emergency.message}</p>
                        {(data.emergency.contacts || []).map((item) => (
                          <p key={item.label} className="text-red-700 text-sm">
                            {item.label}: <strong>{item.value}</strong>
                          </p>
                        ))}
                        {(data.emergency.lines || []).map((line, i) => (
                          <p key={i} className="text-red-700 text-sm">{line}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {data.support && (
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white">
                    <h3 className="text-xl font-bold mb-4">{data.support.title || 'Need Direct Support?'}</h3>
                    <div className="space-y-3">
                      {data.support.phone && (
                        <div className="flex items-center space-x-3">
                          <Phone size={20} />
                          <span>{data.support.phone}</span>
                        </div>
                      )}
                      {data.support.email && (
                        <div className="flex items-center space-x-3">
                          <Mail size={20} />
                          <span>{data.support.email}</span>
                        </div>
                      )}
                      {data.support.hours && (
                        <p className="text-sm opacity-90 mt-4">{data.support.hours}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SiteHelpPage;
